import type { ComponentType, FC } from 'react';
import { observer } from 'mobx-react-lite';
import ConfigurationService, {
  type MergedConfig
} from '@/services/isomorphic/ConfigurationService';
import { ConfigName } from '@/configs';
import { Path as PathType } from '@/type-utils/Path';
import { Language } from '@/constructs/Language';
import { Country } from '@/constructs/Country';
import { EnvironmentService } from '@/services/isomorphic/EnvironmentService';

/**
 * The props that will be added to the wrapped component.
 */
type WithConfigEnabledProps = NonNullable<unknown>;

/**
 * A higher order component that will only render the wrapped component if config is set.
 * In isolated contexts (Storybook, Jest), the config gate is bypassed and the
 * component always renders so that stories and tests are not blocked by
 * config values.
 * @param WrappedComponent - The component to wrap.
 * @param configName - The name of the config which this object represents. Valid.
 * @param configPath - The path to the config value that determines if the component should be enabled.
 * @param language - The language specific for these config values.
 * @param country - The country specific for these config values.
 * @returns The wrapped component with the extra info.
 */
export function WithConfigEnabled<
  T extends ConfigName,
  P,
  Path extends PathType<MergedConfig<T>>
>(
  WrappedComponent: ComponentType<P & WithConfigEnabledProps>,
  configName: T,
  configPath: Path,
  language?: Language,
  country?: Country
): FC<P> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- I'm not quite sure how to type this.
  const ConfigDependantComponent = observer((props: any): any => {
    if (!(typeof __isolatedContext__ !== "undefined")) {
      const enabled = Boolean(
        ConfigurationService.getConfig(
          configName,
          language,
          country
        ).getSetting(configPath)?.value
      );

      if (!enabled) return null;
    }

    // At this point, the props being passed in are the original props the component expects.
    return <WrappedComponent {...props} />;
  });
  // Set the display name for the wrapped component for better debugging.
  ConfigDependantComponent.displayName = `WithConfigEnabled(${WrappedComponent.displayName})`;
  return ConfigDependantComponent;
}
